import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OpenItemsResponse, PaymentAllocationLine, PaymentResponse } from '../../api/types';
import { getPayment } from '../../api/endpoints/payments';
import {
  formatMoney,
  inputPaise,
  moneyInputError,
  paiseToDecimal,
  parsePaise,
  type ReceiptAllocationRow,
} from './allocation';
import { OpenItemsBrowser } from './OpenItemsBrowser';
import { OrderLabel } from './OrderLabel';
import {
  useOpenItemsPaging,
  useSelectedOpenItems,
  type OpenItemsLoader,
} from './useOpenItemsPaging';
import { canAllocateReceiptCredit, reconcileReceiptCredit } from './receiptCredit';
import styles from './Payments.module.css';
import { Button } from '../../components/ui/Button';
import { DialogHelp } from '../../help/DialogHelp';

function row(
  order: OpenItemsResponse['orders'][number],
  previous?: ReceiptAllocationRow,
): ReceiptAllocationRow {
  return {
    shipmentId: order.shipmentId,
    orderDate: order.invoiceDueDate,
    outstandingAmount: order.outstandingAmount,
    amount: previous?.amount ?? '0.00',
    tdsAmount: previous?.tdsAmount ?? '0.00',
  };
}
function hasValue(value: ReceiptAllocationRow) {
  return (
    inputPaise(value.amount) > 0n ||
    inputPaise(value.tdsAmount) > 0n ||
    Boolean(
      moneyInputError(value.amount.trim() || '0') || moneyInputError(value.tdsAmount.trim() || '0'),
    )
  );
}
const eligibleStatus = ['LOADED', 'IN_TRANSIT', 'AT_DROP', 'DELIVERED', 'COMPLETED'];

export function AllocateReceiptCreditDialog({
  payment: initialPayment,
  openItems,
  shipperName,
  onClose,
  onSubmit,
  onReconcile = getPayment,
  onLoadItems,
  onRefresh,
  onConfirmed,
  submitting = false,
  readOnly = false,
}: {
  payment: PaymentResponse;
  openItems: OpenItemsResponse;
  shipperName: string;
  onClose: () => void;
  onSubmit: (paymentId: number, lines: PaymentAllocationLine[]) => Promise<PaymentResponse>;
  onReconcile?: (paymentId: number) => Promise<PaymentResponse>;
  onLoadItems?: OpenItemsLoader;
  onRefresh: () => Promise<unknown>;
  onConfirmed?: (payment: PaymentResponse, lines: PaymentAllocationLine[]) => void;
  submitting?: boolean;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const paging = useOpenItemsPaging(openItems, onLoadItems);
  const selection = useSelectedOpenItems(paging.items, row, hasValue);
  const [payment, setPayment] = useState(initialPayment);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [message, setMessage] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);
  const [attempt, setAttempt] = useState<{
    before: PaymentResponse;
    lines: PaymentAllocationLine[];
    rejected: boolean;
  } | null>(null);
  const selected = [...selection.selected.values()];
  const lines: PaymentAllocationLine[] = selected.map(({ row }) => ({
    shipmentId: row.shipmentId,
    purpose: 'RECEIPT',
    amount: paiseToDecimal(inputPaise(row.amount)),
    tdsAmount: paiseToDecimal(inputPaise(row.tdsAmount)),
  }));
  const cash = lines.reduce((sum, a) => sum + parsePaise(a.amount), 0n);
  const tds = lines.reduce((sum, a) => sum + parsePaise(a.tdsAmount), 0n);
  const credit = parsePaise(payment.unallocatedCredit);
  const error = !canAllocateReceiptCredit(payment, readOnly)
    ? 'This receipt is not eligible for credit allocation.'
    : paging.items.direction !== 'IN' || paging.items.counterpartyId !== payment.counterpartyUserId
      ? 'Open orders do not belong to this receipt’s shipper.'
      : selected.some(
            ({ order }) =>
              !eligibleStatus.includes(order.status) || parsePaise(order.outstandingAmount) <= 0n,
          )
        ? 'A selected order is no longer eligible.'
        : selected.some(
              ({ row }) =>
                moneyInputError(row.amount.trim() || '0') ||
                moneyInputError(row.tdsAmount.trim() || '0'),
            )
          ? 'Enter valid nonnegative cash and TDS amounts.'
          : selected.some(
                ({ row }) =>
                  inputPaise(row.amount) + inputPaise(row.tdsAmount) >
                  parsePaise(row.outstandingAmount),
              )
            ? 'Cash + TDS exceeds order outstanding.'
            : cash > credit
              ? 'Cash allocation exceeds this receipt’s available credit.'
              : null;
  const valid = !error && lines.length > 0 && lines.length <= 50 && !paging.pending;

  const reconcile = async (current: NonNullable<typeof attempt>) => {
    try {
      const fresh = await onReconcile(current.before.id);
      const result = reconcileReceiptCredit(current.before, fresh, current.lines);
      setPayment(fresh);
      if (result === 'confirmed') {
        setFinished(true);
        setAttempt(null);
        setMessage('Allocation confirmed on the receipt. No request was replayed.');
        await onRefresh();
        onConfirmed?.(fresh, current.lines);
      } else if (result === 'unchanged' && current.rejected) {
        setAttempt(null);
        setMessage(
          'The request was rejected and the receipt is unchanged. Check your draft before submitting again.',
        );
        await onRefresh();
      } else {
        setMessage(
          result === 'unchanged'
            ? 'Completion is still uncertain. The receipt is currently unchanged; do not resubmit. Check receipt status again or close for review.'
            : 'Receipt allocations or credit changed. Do not resubmit this draft; close and review the receipt.',
        );
      }
    } catch {
      setMessage(
        'Could not reconcile receipt status. Submission stays blocked. Retry the status check.',
      );
    }
  };
  const submit = async () => {
    if (!valid || inFlight.current || submitting || attempt || finished) return;
    inFlight.current = true;
    setBusy(true);
    setMessage(null);
    const before = payment;
    try {
      const fresh = await onSubmit(before.id, lines);
      if (reconcileReceiptCredit(before, fresh, lines) !== 'confirmed') {
        setAttempt({ before, lines, rejected: false });
        setPayment(fresh);
        setMessage(
          'The server result does not confirm this exact allocation. Do not resubmit; check receipt status or close for review.',
        );
        return;
      }
      setPayment(fresh);
      setFinished(true);
      setMessage('Credit allocated to the selected orders.');
      await onRefresh();
      onConfirmed?.(fresh, lines);
    } catch (cause) {
      const failure = cause as { status?: number; response?: { status?: number } };
      const status = failure?.status ?? failure?.response?.status;
      const current = {
        before,
        lines,
        rejected: Boolean(status && status >= 400 && status < 500 && status !== 408),
      };
      setAttempt(current);
      await reconcile(current);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  return (
    <div className={styles.scrim}>
      <section
        role="dialog"
        aria-modal="true"
        aria-label={t('payments.allocateReceiptCreditDialog.dialogLabel')}
        className={`${styles.dialog} ${styles.allocationDialog}`}
      >
        <header className={`${styles.header} ${styles.orderHeader}`}>
          <div>
            <h2>{t('payments.allocateReceiptCreditDialog.title', { paymentId: payment.id })}</h2>
            <p>
              {t('payments.allocateReceiptCreditDialog.shipperLine', { name: shipperName, userId: payment.counterpartyUserId })}
            </p>
            <p>{t('payments.allocateReceiptCreditDialog.noNewTransfer')}</p>
          </div>
          <div className={`${styles.actions} ${styles.orderActions}`}>
            <DialogHelp context="allocate-credit" title={t('payments.allocateReceiptCreditDialog.title', { paymentId: payment.id })} />
            <Button variant="secondary" size="sm" disabled={busy || submitting} onClick={onClose}>
              {finished ? 'Close' : 'Cancel'}
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!valid || busy || submitting || Boolean(attempt) || finished}
              onClick={() => void submit()}
            >
              {busy || submitting ? 'Checking / saving…' : 'Allocate credit'}
            </Button>
          </div>
        </header>
        <div className={styles.body}>
          <div className={styles.card}>
            <p>
              {t('payments.allocateReceiptCreditDialog.receiptAmount', { amount: formatMoney(parsePaise(payment.amount)) })} ·{' '}
              {payment.method ?? 'Transfer'} · {payment.reference ?? 'No reference'}
            </p>
            <strong>{t('payments.allocateReceiptCreditDialog.availableCredit', { amount: formatMoney(credit) })}</strong>
            <p>
              {t('payments.allocateReceiptCreditDialog.cashExplainer')}
            </p>
          </div>
          <fieldset
            disabled={readOnly || busy || submitting || Boolean(attempt) || finished}
            className={`${styles.card} ${styles.allocationOrders}`}
          >
            <legend>{t('payments.allocateReceiptCreditDialog.allocateTo')}</legend>
            <OpenItemsBrowser paging={paging} />
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('payments.allocateReceiptCreditDialog.colOrder')}</th>
                    <th>{t('payments.allocateReceiptCreditDialog.colInvoice')}</th>
                    <th>{t('payments.allocateReceiptCreditDialog.colOutstanding')}</th>
                    <th>{t('payments.allocateReceiptCreditDialog.colCash')}</th>
                    <th>{t('payments.allocateReceiptCreditDialog.colNewTds')}</th>
                  </tr>
                </thead>
                <tbody>
                  {selection.orders.map((order) => {
                    const value =
                      selection.rows.find((r) => r.shipmentId === order.shipmentId) ?? row(order);
                    const update = (field: 'amount' | 'tdsAmount', amount: string) =>
                      selection.setRows(
                        selection.rows.map((r) =>
                          r.shipmentId === order.shipmentId ? { ...r, [field]: amount } : r,
                        ),
                      );
                    return (
                      <tr
                        key={order.shipmentId}
                        aria-selected={
                          inputPaise(value.amount) > 0n || inputPaise(value.tdsAmount) > 0n
                        }
                      >
                        <td>
                          <OrderLabel {...order} newTab />
                          {(inputPaise(value.amount) > 0n || inputPaise(value.tdsAmount) > 0n) && (
                            <div role="status">
                              {t('payments.allocateReceiptCreditDialog.selectedReady', {
                                amount: formatMoney(inputPaise(value.amount) + inputPaise(value.tdsAmount)),
                              })}
                            </div>
                          )}
                          <div>
                            {t('payments.allocateReceiptCreditDialog.outstandingAfter', {
                              amount: formatMoney(
                                parsePaise(order.outstandingAmount) -
                                  inputPaise(value.amount) -
                                  inputPaise(value.tdsAmount),
                              ),
                            })}
                          </div>
                        </td>
                        <td>{order.invoiceNumber ?? 'Uninvoiced estimate'}</td>
                        <td>
                          <div className={styles.allocationAmount}>
                            {formatMoney(parsePaise(order.outstandingAmount))}
                          </div>
                          <div className={styles.allocationTools}>
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={Boolean(moneyInputError(value.tdsAmount.trim() || '0'))}
                              aria-label={`Fill cash for #${order.shipmentId}`}
                              onClick={() => {
                                const outstanding =
                                  parsePaise(order.outstandingAmount) - inputPaise(value.tdsAmount);
                                const available = credit - (cash - inputPaise(value.amount));
                                const fill = outstanding < available ? outstanding : available;
                                update('amount', paiseToDecimal(fill > 0n ? fill : 0n));
                              }}
                            >
                              {t('payments.allocateReceiptCreditDialog.fill')}
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              aria-label={`Clear allocation for #${order.shipmentId}`}
                              onClick={() =>
                                selection.setRows(
                                  selection.rows.map((r) =>
                                    r.shipmentId === order.shipmentId
                                      ? { ...r, amount: '0.00', tdsAmount: '0.00' }
                                      : r,
                                  ),
                                )
                              }
                            >
                              {t('payments.allocateReceiptCreditDialog.clear')}
                            </Button>
                          </div>
                        </td>
                        <td>
                          <input
                            className={styles.moneyInput}
                            aria-label={`Cash for #${order.shipmentId}`}
                            value={value.amount}
                            onChange={(e) => update('amount', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className={styles.moneyInput}
                            aria-label={`TDS for #${order.shipmentId}`}
                            value={value.tdsAmount}
                            onChange={(e) => update('tdsAmount', e.target.value)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </fieldset>
          <div className={styles.allocationTotals}>
            <p>
              {t('payments.allocateReceiptCreditDialog.totals', { cash: formatMoney(cash), tds: formatMoney(tds) })}
            </p>
            <p>{t('payments.allocateReceiptCreditDialog.remainingCredit', { amount: formatMoney(finished ? credit : credit - cash) })}</p>
          </div>
          {error && !finished && (
            <p role="alert" className={styles.error}>
              {error}
            </p>
          )}
          {message && <p role="status">{message}</p>}
          {attempt && (
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={async () => {
                if (inFlight.current) return;
                inFlight.current = true;
                setBusy(true);
                await reconcile(attempt);
                inFlight.current = false;
                setBusy(false);
              }}
            >
              {t('payments.allocateReceiptCreditDialog.checkAgain')}
            </Button>
          )}
        </div>
      </section>
    </div>
  );
}
